/**
 * BullMQ queue names and job payloads (server spec §3.2, §7.5).
 */
import { z } from 'zod';
import { Id } from './schemas/common.js';
import { ProcessingStepName } from './schemas/versions.js';
import { JobKind } from './schemas/versions.js';

export const QUEUES = {
  PROCESS_IMAGE: 'process-image',
  PROCESS_VIDEO: 'process-video',
  PROCESS_AUDIO: 'process-audio',
  PROCESS_DOCUMENT: 'process-document',
  AI_INDEX: 'ai-index',
  HOUSEKEEPING: 'housekeeping',
  /** Server-side async jobs: bulk zip downloads, audit CSV exports (`jobs` table). */
  JOBS: 'jobs',
} as const;

export type QueueName = (typeof QUEUES)[keyof typeof QUEUES];
export const QUEUE_NAMES = Object.values(QUEUES) as readonly QueueName[];

export type ProcessingQueueName = Exclude<QueueName, 'housekeeping' | 'jobs'>;

/** Which processing queue handles an asset type (videos without a video stream are re-routed to audio by the worker). */
export function queueForAssetType(
  type: 'image' | 'video' | 'audio' | 'document' | 'other',
): ProcessingQueueName {
  switch (type) {
    case 'image':
      return QUEUES.PROCESS_IMAGE;
    case 'video':
      return QUEUES.PROCESS_VIDEO;
    case 'audio':
      return QUEUES.PROCESS_AUDIO;
    case 'document':
      return QUEUES.PROCESS_DOCUMENT;
    default:
      return QUEUES.PROCESS_IMAGE;
  }
}

export const JobPriority = z.enum(['upload', 'reprocess']);
export type JobPriority = z.infer<typeof JobPriority>;
/** BullMQ: lower = higher priority. */
export const JOB_PRIORITY_VALUE: Readonly<Record<JobPriority, number>> = {
  upload: 1,
  reprocess: 10,
};

/** Payload for every queue except housekeeping. */
export const ProcessJob = z.object({
  version_id: Id,
  asset_id: Id,
  workspace_id: Id,
  /** Redis `INCR reproc:{version_id}`; 0 for the first run. */
  reprocess_seq: z.int().min(0),
  priority: JobPriority,
  /** Subset for partial re-run. */
  steps: z.array(ProcessingStepName).optional(),
});
export type ProcessJob = z.infer<typeof ProcessJob>;

export const HOUSEKEEPING_JOBS = [
  'purge-soft-deleted',
  'process-tombstones',
  'expire-upload-sessions',
  'reconcile-storage',
  'trim-event-streams',
  'delete-temp-renditions',
  'merge-face-clusters',
  'retry-ai-stalled',
  'audit-retention',
  'expire-jobs',
  // Release 2 (nightly; docs/specs/release2-contract.md "Nightly jobs")
  'count-smart-boards',
  'expire-rights',
  'apply-retention',
  // Release 3 (every 5 min; docs/specs/release3-contract.md "Nightly / periodic jobs"): e-mails + digests, lane A
  'deliver-notifications',
] as const;
export type HousekeepingJobName = (typeof HOUSEKEEPING_JOBS)[number];

export const HousekeepingJob = z.object({
  name: z.enum(HOUSEKEEPING_JOBS),
  params: z.record(z.string(), z.unknown()).optional(),
});
export type HousekeepingJob = z.infer<typeof HousekeepingJob>;

/** Payload of the `jobs` queue: one BullMQ job per `jobs` row, id `job:{job_id}`. */
export const JobsQueueJob = z.object({
  job_id: Id,
  workspace_id: Id,
  kind: JobKind,
});
export type JobsQueueJob = z.infer<typeof JobsQueueJob>;
export const jobsJobId = (jobId: string): string => `job-${jobId}`; // BullMQ forbids ':' in custom ids

/**
 * Release 3 EXP-1 (lane C): one on-demand image export. Runs on the `jobs` queue as a `jobs` row of kind
 * `export_render` (`JobsQueueJob`); `params` of the row carry this payload. Idempotent on `transform_key`.
 */
export const EXPORT_RENDER_JOB_NAME = 'export-render';
export const ExportRenderJob = z.object({
  version_id: Id,
  /** `ImageTransform` (release3.ts); typed loosely here because queues.ts is imported by the schemas' consumers. */
  transform: z.record(z.string(), z.unknown()),
  /** sha256 of version sha256 + canonical transform + EXPORT_ENGINE_REV (release3.ts `canonicalTransform`). */
  transform_key: z.string().regex(/^[0-9a-f]{64}$/),
});
export type ExportRenderJob = z.infer<typeof ExportRenderJob>;
export const exportRenderJobId = (transformKey: string): string => `export-${transformKey}`;

/** Finished job results (zip / CSV objects) are kept this long, then removed by `expire-jobs`. */
export const JOB_RESULT_TTL_HOURS = 24;

/** Idempotent job ids: `proc:{version_id}:{seq}` / `ai:{version_id}:{seq}`. */
export const processJobId = (versionId: string, reprocessSeq: number): string =>
  `proc:${versionId}:${reprocessSeq}`;
export const aiIndexJobId = (versionId: string, reprocessSeq: number): string =>
  `ai:${versionId}:${reprocessSeq}`;
export const reprocessCounterKey = (versionId: string): string => `reproc:${versionId}`;

export const DEFAULT_JOB_OPTIONS = {
  attempts: 3,
  backoff: { type: 'exponential', delay: 10_000 },
  removeOnComplete: { age: 86_400, count: 5000 },
  removeOnFail: { age: 7 * 86_400 },
} as const;

/** Default worker concurrency per queue (env-overridable; video = max(1, floor(cpus/2))). */
export const DEFAULT_CONCURRENCY: Readonly<Record<QueueName, number | 'auto'>> = {
  'process-image': 4,
  'process-video': 'auto',
  'process-audio': 2,
  'process-document': 2,
  'ai-index': 2,
  housekeeping: 1,
  jobs: 2,
};

/** Processing timeouts (ms). Video: max(10 min, 3 × duration) capped at 4 h. */
export const JOB_TIMEOUT_MS = {
  image: 5 * 60_000,
  document: 10 * 60_000,
  audio: 30 * 60_000,
  videoMin: 10 * 60_000,
  videoMax: 4 * 3_600_000,
} as const;

export function videoJobTimeoutMs(durationMs: number): number {
  return Math.min(JOB_TIMEOUT_MS.videoMax, Math.max(JOB_TIMEOUT_MS.videoMin, 3 * durationMs));
}
